/**
 * TkSearchEngine — shared predictive-search engine for the TK storefront.
 *
 * ONE implementation of the fetch / cache / abort / spell-retry / render
 * pipeline, consumed by both search surfaces:
 *   - blocks/ai_gen_block_3c36854.liquid (homepage hero search bar)
 *   - snippets/tk-search-overlay.liquid  (site-wide search overlay)
 *
 * Both surfaces previously carried verbatim ~60-line copies of this logic,
 * and the copies drifted (opposite escaping conventions produced a
 * double-escaped message bug). Fixes now land here once.
 *
 * Usage:
 *   var engine = window.TkSearchEngine.create({
 *     results: resultsElement,          // container painted with section HTML
 *     statusClass: 'tk-search__status', // consumer's status <div> class
 *     spinnerAnim: 'tk-search-spin',    // consumer's spin @keyframes name
 *     onShow: function () {}            // called whenever content is painted
 *   });
 *   engine.search(query);        // consumer debounces its input events
 *   engine.reset();              // abort in-flight work, forget last query
 *   engine.nav(event, input);    // ArrowUp/ArrowDown/Enter over results
 *   engine.hasVisibleContent();  // any painted child actually visible?
 */
(function () {
  'use strict';

  if (window.TkSearchEngine) return;

  var SECTION_ID = 'predictive-search-tk';
  var CACHE_TTL_MS = 10 * 60 * 1000; // stale prices are worse than a refetch
  var CACHE_MAX = 48;
  var NAV_SELECTOR = '.tk-ps-pill, .tk-ps-coll, .tk-ps-item';

  function esc(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function create(opts) {
    var results = opts.results;
    var statusClass = opts.statusClass;
    var spinnerAnim = opts.spinnerAnim;
    var onShow = opts.onShow || function () {};

    var lastQuery = '';
    var cache = new Map(); // query -> parsed result; repeats render instantly
    var abortCtrl = null;  // in-flight request, cancelled when superseded

    function statusHTML(inner) {
      return '<div class="' + statusClass + '">' + inner + '</div>';
    }

    // All dynamic text is escaped HERE — callers pass raw strings.
    function showStatus(text) {
      results.innerHTML = statusHTML(esc(text));
      onShow();
    }

    function showLoading() {
      // Keep the previous results on screen while the next ones load —
      // swapping in a spinner on every keystroke feels slower than it is.
      if (results.querySelector(NAV_SELECTOR)) return;
      results.innerHTML = statusHTML(
        '<span style="display:inline-block;width:16px;height:16px;border:2px solid var(--tk-hairline);' +
          'border-top-color:var(--tk-gold);border-radius:50%;animation:' + spinnerAnim + ' 0.7s linear infinite;' +
          'vertical-align:middle;margin-right:6px"></span>Searching…'
      );
    }

    function hasVisibleContent() {
      return Array.prototype.some.call(results.children, function (c) {
        return c.offsetHeight > 0;
      });
    }

    function paintResults(html, query) {
      results.innerHTML = html;
      // Safety net: if everything in the response is hidden at this
      // breakpoint, an open results area reads as broken (a bare gold
      // sliver) — show the explicit no-results note instead.
      if (!hasVisibleContent()) {
        results.innerHTML = statusHTML('No results for “' + esc(query) + '”.');
      }
      onShow();
    }

    function correctionNote(corrected, original) {
      return (
        '<div class="tk-ps-correction" role="status">Showing results for <strong>“' + esc(corrected) +
        '”</strong><span class="tk-ps-correction-sub">Nothing matched “' + esc(original) + '”</span></div>'
      );
    }

    async function fetchSection(query, signal) {
      var cached = cache.get(query);
      if (cached && Date.now() - cached.t < CACHE_TTL_MS) return cached;
      if (cached) cache.delete(query);
      var url =
        '/search/suggest?q=' + encodeURIComponent(query) +
        '&resources[type]=product,query,collection&resources[limit]=10&resources[limit_scope]=each' +
        '&section_id=' + SECTION_ID;
      var res = await fetch(url, { signal: signal });
      if (!res.ok) throw new Error('Network error');
      var text = await res.text();
      var doc = new DOMParser().parseFromString(text, 'text/html');
      var el = doc.querySelector('#shopify-section-' + SECTION_ID);
      var result = {
        t: Date.now(),
        html: el ? el.innerHTML.trim() : '',
        // Products only (not .tk-ps-coll) — a collection-only match should
        // still get a shot at typo correction finding actual product rows.
        hasProducts: !!(el && el.querySelector('.tk-ps-item'))
      };
      if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
      cache.set(query, result);
      return result;
    }

    async function search(query) {
      if (query === lastQuery) {
        onShow();
        return;
      }
      lastQuery = query;
      if (abortCtrl) abortCtrl.abort();
      var ctrl = (abortCtrl = new AbortController());
      showLoading();
      onShow();
      try {
        var result = await fetchSection(query, ctrl.signal);
        if (query !== lastQuery) return; // stale — a newer query took over

        // No product rows? Retry with a typo-corrected query ("chiken" -> "chicken").
        if (!result.hasProducts && window.TkSearchSpell) {
          var corr = await window.TkSearchSpell.correct(query);
          if (query !== lastQuery) return;
          if (corr.changed) {
            var alt = await fetchSection(corr.query, ctrl.signal);
            if (query !== lastQuery) return;
            if (alt.hasProducts) {
              paintResults(correctionNote(corr.query, query) + alt.html, query);
              return;
            }
          }
        }

        if (!result.html) {
          showStatus('No results for “' + query + '”.');
          return;
        }
        paintResults(result.html, query);
      } catch (_) {
        if (query !== lastQuery) return; // aborted — the newer query owns the UI
        lastQuery = ''; // let the shopper retry the same query after a failure
        showStatus('Something went wrong — please try again.');
      }
    }

    function setActive(items, target, input) {
      items.forEach(function (i) {
        i.removeAttribute('aria-selected');
      });
      if (target) {
        target.setAttribute('aria-selected', 'true');
        target.scrollIntoView({ block: 'nearest' });
      }
      if (input) input.focus();
    }

    /**
     * Keyboard navigation over the painted results. Returns true when the
     * event was handled; Escape and default Enter (form submit) stay with
     * the consumer.
     */
    function nav(event, input) {
      var items = Array.prototype.slice.call(results.querySelectorAll(NAV_SELECTOR));
      if (!items.length) return false;
      var cur = items.filter(function (i) {
        return i.getAttribute('aria-selected') === 'true';
      })[0];
      var idx = cur ? items.indexOf(cur) : -1;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(items, items[idx < items.length - 1 ? idx + 1 : 0], input);
        return true;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(items, items[idx > 0 ? idx - 1 : items.length - 1], input);
        return true;
      }
      if (event.key === 'Enter' && cur) {
        event.preventDefault();
        var link = cur.tagName === 'A' ? cur : cur.querySelector('a');
        if (link) link.click();
        return true;
      }
      return false;
    }

    function reset() {
      if (abortCtrl) abortCtrl.abort();
      lastQuery = '';
    }

    return {
      search: search,
      reset: reset,
      nav: nav,
      hasVisibleContent: hasVisibleContent
    };
  }

  window.TkSearchEngine = { create: create };
})();
